// Small display helpers. All null/undefined-tolerant.

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  // A bare `YYYY-MM-DD` (as the revenue report returns) is parsed by `new Date()`
  // as UTC midnight; `toLocaleDateString()` in a UTC-west timezone then rolls it
  // back to the previous day. Parse the calendar date in the LOCAL timezone so the
  // displayed day matches the reported day exactly.
  const dateOnly = DATE_ONLY_RE.exec(value);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    const local = new Date(Number(y), Number(m) - 1, Number(d));
    if (Number.isNaN(local.getTime())) return value;
    return local.toLocaleDateString();
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
}

// Money arrives as a decimal string (backend Numeric(18,4)). We pass the STRING
// straight to Intl.NumberFormat: the Intl NumberFormat V3 API accepts a decimal
// string and formats it at full precision (no IEEE-754 `Number()` rounding, so no
// dropped cent on large totals), while Intl still applies correct localization and
// ISO-4217 minor units — USD/EUR 2dp, JPY 0dp, KWD 3dp, plus symbol placement,
// grouping separators and RTL per locale. Supported across Shopify admin's
// evergreen browsers (Chrome 106+ / Safari 15.4+ / Firefox 116+).
export function formatMoney(amount: string | null | undefined, currency: string): string {
  if (amount === null || amount === undefined) return "—";
  const raw = String(amount).trim();
  // Only pass well-formed decimal strings to Intl; anything else echoes verbatim.
  if (!/^-?\d+(?:\.\d+)?$/.test(raw)) return `${amount} ${currency}`;
  // TS lib types still type format() as (number); the V3 string overload is valid
  // at runtime, so cast the decimal string through.
  const value = raw as unknown as number;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
  } catch {
    // Unknown/invalid ISO currency code — degrade to a grouped decimal + raw code,
    // still at full precision (string passed through, no Number()).
    return `${new Intl.NumberFormat(undefined).format(value)} ${currency}`;
  }
}

export function titleCase(value: string): string {
  return value
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
